import React from "react";
import CircularProgress from "./CircularProgress";

interface Props {
  videoRef: React.RefObject<HTMLVideoElement>;
  cameraVideoRef: React.RefObject<HTMLVideoElement>;
  currentRep: number;
  countReps: number;
  currentSet: number;
  countSets: number;
  isResting: boolean;
  onFinish: () => void;
  onRestart: () => void;
  isFinishDisabled?: boolean;
  t: (_key: string) => string;
  restProgress: number;
  timeBetweenSets: number;
  isUploading?: boolean;
  videoSrc?: string;
}

const LandscapeModeUI: React.FC<Props> = ({
  videoRef,
  cameraVideoRef,
  currentRep,
  countReps,
  currentSet,
  countSets,
  isResting,
  onFinish,
  onRestart,
  isFinishDisabled = false,
  t,
  restProgress,
  timeBetweenSets,
  isUploading = false,
  videoSrc,
}) => {
  const repProgress = countReps > 0 ? (currentRep / countReps) * 100 : 0;
  const setProgress = countSets > 0 ? (currentSet / countSets) * 100 : 0;

  const remainingRestTime = Math.ceil(
    timeBetweenSets - (restProgress / 100) * timeBetweenSets
  );

  return (
    <div className="relative w-screen h-screen bg-black flex items-center justify-center overflow-hidden">
      <video
        ref={videoRef}
        src={videoSrc}
        muted
        loop
        playsInline
        autoPlay
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
      />

      {/* Circular UI overlay */}
      <div className="absolute top-6 left-6 z-10 space-y-4">
        <CircularProgress
          size={64}
          strokeWidth={4}
          progress={repProgress}
          text={
            <span className="text-sm font-bold">
              {currentRep}/{countReps}
            </span>
          }
          progressColor="stroke-orange-500"
        />
        <CircularProgress
          size={64}
          strokeWidth={4}
          progress={setProgress}
          text={
            <span className="text-sm font-bold">
              {currentSet}/{countSets}
            </span>
          }
          progressColor="stroke-green-500"
        />
        {isResting && (
          <CircularProgress
            size={64}
            strokeWidth={4}
            progress={restProgress}
            text={
              <span className="text-lg font-bold">{remainingRestTime}s</span>
            }
            progressColor="stroke-blue-400"
          />
        )}
      </div>

      {/* Camera overlay */}
      <video
        ref={cameraVideoRef}
        autoPlay
        playsInline
        muted
        className="absolute top-6 right-6 w-1/4 h-auto rounded-md border-2 border-white z-10"
      />

      {/* Action Buttons */}
      <div className="absolute bottom-6 flex w-full justify-center items-center gap-4 z-10 px-4">
        <button
          onClick={onFinish}
          disabled={isFinishDisabled}
          className={`flex-1 max-w-xs px-6 py-2 rounded-lg text-base md:text-lg font-bold transition shadow-lg
            ${
              isFinishDisabled
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-orange-600 hover:bg-orange-700 text-white"
            }`}
        >
          {t("button.finish")}
        </button>
        <button
          onClick={onRestart}
          className={`flex-1 max-w-xs px-6 py-2 rounded-lg text-base md:text-lg font-bold shadow-lg ${
            isUploading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gray-100 hover:bg-gray-300 text-gray-800"
          }`}
          disabled={isUploading}
        >
          {t("button.restart")}
        </button>
      </div>
    </div>
  );
};

export default LandscapeModeUI;
